import React, { useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, Video, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CHUNK_SIZE = 1.5 * 1024 * 1024; // 1.5 MB per chunk — stays under proxy body limit

export function absUrl(url) {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${BACKEND_URL}${url}`;
}

/**
 * UploadWidget — controlled component.
 * Props:
 *  - value: current URL (relative /api/media/... or absolute)
 *  - onChange: (newUrl) => void
 *  - accept: "image" | "video" | "media" (default "image")
 *  - label: string label above field
 *  - testId: base data-testid
 */
export function UploadWidget({ value, onChange, accept = "image", label = "Image", testId = "upload" }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState("");
    const [urlDraft, setUrlDraft] = useState(value || "");

    React.useEffect(() => { setUrlDraft(value || ""); }, [value]);

    const acceptAttr = accept === "video" ? "video/*" : accept === "media" ? "image/*,video/*" : "image/*";

    const handleFile = async (file) => {
        if (!file) return;
        setUploading(true);
        setError("");
        setProgress(0);
        try {
            const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

            if (totalChunks <= 1) {
                // Small file — single upload
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                onChange?.(data.url);
            } else {
                // Large file — chunked upload
                const uploadId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
                let finalUrl = null;

                for (let i = 0; i < totalChunks; i++) {
                    const start = i * CHUNK_SIZE;
                    const end = Math.min(start + CHUNK_SIZE, file.size);
                    const chunk = file.slice(start, end);

                    const fd = new FormData();
                    fd.append("upload_id", uploadId);
                    fd.append("chunk_index", String(i));
                    fd.append("total_chunks", String(totalChunks));
                    fd.append("filename", file.name);
                    fd.append("content_type", file.type || "application/octet-stream");
                    fd.append("file", chunk, file.name);

                    const { data } = await api.post("/admin/upload-chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    setProgress(Math.round(((i + 1) / totalChunks) * 100));

                    if (data.url) finalUrl = data.url;
                }
                onChange?.(finalUrl);
            }
        } catch (e) {
            setError(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed. Please try again.");
        } finally {
            setUploading(false);
            setProgress(0);
        }
    };

    const clearValue = () => onChange?.("");

    const isYoutube = urlDraft && (urlDraft.includes("youtube.com") || urlDraft.includes("youtu.be"));
    const kind = isYoutube ? "youtube" : (urlDraft && urlDraft.match(/\.(mp4|mov|webm|ogv|avi|mpeg|mpg)(\?.*)?$/i) ? "video" : "image");

    return (
        <div className="space-y-2" data-testid={testId}>
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</label>
            <div className="rounded-lg border border-input bg-white p-3">
                {urlDraft ? (
                    <div className="relative">
                        <div className="rounded-md overflow-hidden bg-secondary/40 aspect-video flex items-center justify-center">
                            {kind === "youtube" ? (
                                <iframe title="Video preview" src={urlDraft} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                            ) : kind === "video" ? (
                                <video src={absUrl(urlDraft)} controls className="max-h-full max-w-full" />
                            ) : (
                                <img src={absUrl(urlDraft)} alt="preview" className="max-h-full max-w-full object-contain" />
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={clearValue}
                            data-testid={`${testId}-clear`}
                            className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/95 shadow border border-border flex items-center justify-center text-foreground/70 hover:text-red-600"
                            aria-label="Clear"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        data-testid={`${testId}-dropzone`}
                        className="w-full rounded-md border-2 border-dashed border-input bg-secondary/30 hover:border-primary/40 hover:bg-secondary/60 transition-colors flex flex-col items-center justify-center py-10 text-muted-foreground"
                    >
                        {accept === "video" ? <Video className="h-6 w-6" /> : <ImageIcon className="h-6 w-6" />}
                        <span className="mt-2 text-sm font-semibold">Click to upload {accept === "video" ? "a video" : accept === "media" ? "an image or video" : "an image"}</span>
                        <span className="mt-1 text-xs">{accept === "video" ? "MP4, WebM, MOV up to 200 MB" : "or paste a URL below"}</span>
                    </button>
                )}

                {/* Upload progress bar */}
                {uploading && (
                    <div className="mt-3 space-y-1.5">
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>Uploading{progress > 0 ? ` — ${progress}%` : "..."}</span>
                        </div>
                        <div className="h-1.5 w-full rounded-full bg-secondary overflow-hidden">
                            <div
                                className="h-full bg-primary rounded-full transition-all duration-300"
                                style={{ width: `${progress || 10}%` }}
                            />
                        </div>
                    </div>
                )}

                <input
                    ref={inputRef}
                    type="file"
                    accept={acceptAttr}
                    className="hidden"
                    data-testid={`${testId}-file-input`}
                    onChange={(e) => handleFile(e.target.files?.[0])}
                />
                <div className="mt-3 flex items-center gap-2">
                    <input
                        type="text"
                        value={urlDraft}
                        onChange={(e) => setUrlDraft(e.target.value)}
                        onBlur={() => onChange?.(urlDraft)}
                        placeholder={accept === "video" ? "https://www.youtube.com/embed/... or /api/media/..." : "/api/media/... or https://..."}
                        data-testid={`${testId}-url-input`}
                        className="flex-1 rounded-md border border-input bg-white px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading}
                        data-testid={`${testId}-upload-btn`}
                        className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
                    >
                        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                        {uploading ? "Uploading..." : "Upload"}
                    </button>
                </div>
                {error && <div className="mt-2 text-xs text-red-600" data-testid={`${testId}-error`}>{error}</div>}
            </div>
        </div>
    );
}

export default UploadWidget;
